import React, {useEffect, useMemo, useRef, useState} from "react";
import styles from "./Catalog.module.css";
import Cards from "./components/Cards.jsx";
import { getCountCard, getOneShots, getCampaigns } from "@/entities/catalogCard/api/CatalogApi.js";
import { FadeIn } from "@/shared/ui/animations";
import useIsMobile from "@/shared/lib/hooks/useIsMobile";
import FiltersDropdown from "@/features/Catalog/ui/FiltersDropdown.jsx";
import Pagination from "@/features/Catalog/ui/Pagination.jsx";
import { getSystems } from "@/entities/gameSystem/api/gameSystemApi.js";

const CatalogSection = () => {
    // ===== ФИЛЬТРЫ =====
    const [systems, setSystems] = useState([]);                 // [{id,label}]
    const [selectedSystems, setSelectedSystems] = useState([]); // ["D&D 5E", "Cyberpunk Red"] (id = name)
    const [selectedFormats, setSelectedFormats] = useState([]); // ["ONLINE","OFFLINE"]
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);

    const formats = [
        { id: "ONLINE", label: "Online" },
        { id: "OFFLINE", label: "Offline" },
        { id: "ONLINE_OFFLINE", label: "Online/offline" },
    ];

    // ===== состояние каталога =====
    const [amountOneShots, setAmountOneShots] = useState(0);
    const [amountCampaigns, setAmountCampaigns] = useState(0);
    const [isCampaignsSelected, setIsCampaignsSelected] = useState(false);

    const [currentPageOneShots, setCurrentPageOneShots] = useState(1);
    const [currentPageCampaigns, setCurrentPageCampaigns] = useState(1);

    const [isLoadingOneShots, setIsLoadingOneShots] = useState(false);
    const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);

    const [error, setError] = useState(null);

    const [oneShots, setOneShots] = useState([]);
    const [campaigns, setCampaigns] = useState([]);

    const isMobile = useIsMobile();
    const pageSize = isMobile ? 3 : 6;

    const tabsSliderRef = useRef(null);

    // =========================
    // ✅ DEBOUNCE ФИЛЬТРОВ
    // =========================
    const [debouncedSystems, setDebouncedSystems] = useState(selectedSystems);
    const [debouncedFormats, setDebouncedFormats] = useState(selectedFormats);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSystems(selectedSystems), 600);
        return () => clearTimeout(t);
    }, [selectedSystems]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedFormats(selectedFormats), 600);
        return () => clearTimeout(t);
    }, [selectedFormats]);

    // filters именно debounced
    const filters = useMemo(() => {
        return {
            systemFilters: debouncedSystems,
            gameFormatFilters: debouncedFormats,
        };
    }, [debouncedSystems, debouncedFormats]);

    // total pages
    const totalPagesOneShots = useMemo(() => {
        return Math.max(1, Math.ceil((amountOneShots || 0) / pageSize));
    }, [amountOneShots, pageSize]);

    const totalPagesCampaigns = useMemo(() => {
        return Math.max(1, Math.ceil((amountCampaigns || 0) / pageSize));
    }, [amountCampaigns, pageSize]);

    // =========================
    // ✅ загрузка систем 1 раз
    // =========================
    useEffect(() => {
        const loadSystems = async () => {
            try {
                setIsLoadingFilters(true);

                const sysRes = await getSystems();

                // поддерживаем 2 варианта: массив или axios response
                const list = Array.isArray(sysRes)
                    ? sysRes
                    : Array.isArray(sysRes?.data)
                        ? sysRes.data
                        : [];

                // делаем id = name (чтобы улетало в systemFilter строкой)
                const normalizedSystems = list.map((s) => ({
                    id: s.name,
                    label: s.name,
                }));

                setSystems(normalizedSystems);
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoadingFilters(false);
            }
        };

        loadSystems();
    }, []);

    // =========================
    // ✅ загрузка каталога при:
    // - смене pageSize
    // - смене debounced filters
    // =========================
    useEffect(() => {
        const loadCatalog = async () => {
            try {
                setIsLoadingOneShots(true);
                setIsLoadingCampaigns(true);
                setError(null);

                // сброс страниц при изменении фильтров / pageSize
                setCurrentPageOneShots(1);
                setCurrentPageCampaigns(1);

                const countRes = await getCountCard(filters);
                setAmountOneShots(countRes?.data?.oneshot || 0);
                setAmountCampaigns(countRes?.data?.campaign || 0);

                const [oneShotsRes, campaignsRes] = await Promise.all([
                    getOneShots(0, pageSize, filters),
                    getCampaigns(0, pageSize, filters),
                ]);

                setOneShots(oneShotsRes?.data?.content || []);
                setCampaigns(campaignsRes?.data?.content || []);
            } catch (err) {
                console.error(err);
                setError("Ошибка загрузки");
            } finally {
                setIsLoadingOneShots(false);
                setIsLoadingCampaigns(false);
            }
        };

        loadCatalog();
        // ВАЖНО: тут filters (debounced), а не selectedSystems/selectedFormats
    }, [pageSize, filters]);

    // вкладки
    const switchToOneShots = () => {
        setIsCampaignsSelected(false);
        scrollToActiveTab(false);
    };

    const switchToCampaigns = () => {
        setIsCampaignsSelected(true);
        scrollToActiveTab(true);
    };

    // смена страницы (используем debounced filters)
    const handleChangePageOneShots = async (page) => {
        setIsLoadingOneShots(true);
        setError(null);
        try {
            const res = await getOneShots(page - 1, pageSize, filters);
            setOneShots(res?.data?.content || []);
            setCurrentPageOneShots(page);
        } catch (e) {
            console.error(e);
            setError("Ошибка загрузки");
        } finally {
            setIsLoadingOneShots(false);
        }
    };

    const handleChangePageCampaigns = async (page) => {
        setIsLoadingCampaigns(true);
        setError(null);
        try {
            const res = await getCampaigns(page - 1, pageSize, filters);
            setCampaigns(res?.data?.content || []);
            setCurrentPageCampaigns(page);
        } catch (e) {
            console.error(e);
            setError("Ошибка загрузки");
        } finally {
            setIsLoadingCampaigns(false);
        }
    };

    const currentCards = isCampaignsSelected ? campaigns : oneShots;
    const currentType = isCampaignsSelected ? "campaign" : "oneShot";
    const currentPage = isCampaignsSelected ? currentPageCampaigns : currentPageOneShots;
    const totalPages = isCampaignsSelected ? totalPagesCampaigns : totalPagesOneShots;
    const changePage = isCampaignsSelected ? handleChangePageCampaigns : handleChangePageOneShots;


    const scrollToActiveTab = (isCampaign) => {
        if (!tabsSliderRef.current) return;

        setTimeout(() => {
            const slider = tabsSliderRef.current;
            const buttons = slider.querySelectorAll('.btnTabScroll');

            if (buttons.length < 2) return;

            const activeButton = isCampaign ? buttons[1] : buttons[0];
            if (!activeButton) return;

            const buttonRect = activeButton.getBoundingClientRect();
            const sliderRect = slider.getBoundingClientRect();

            const scrollLeft = buttonRect.left - sliderRect.left + slider.scrollLeft - (sliderRect.width - buttonRect.width) / 2;
            const maxScroll = slider.scrollWidth - slider.clientWidth;
            const safeScrollLeft = Math.max(0, Math.min(scrollLeft, maxScroll));

            slider.scrollTo({
                left: safeScrollLeft,
                behavior: 'smooth'
            });
        }, 100);
    };

    return (
        <section className="container-custom" id="games">
            {/* Фильтры */}
            <FadeIn delay={100}>
                <FiltersDropdown
                    categories={systems}
                    selectedCategories={selectedSystems}
                    onChangeCategories={setSelectedSystems}
                    formats={formats}
                    selectedFormats={selectedFormats}
                    onChangeFormats={setSelectedFormats}
                    isLoading={isLoadingFilters}
                />
            </FadeIn>

            {/* Табы */}
            <div className={`row g-0`}>
                <div className={styles.tabsSliderContainer} ref={tabsSliderRef}>
                    <div className={`${styles.tabsSlider} ${styles.buttonsTabsWrapper}`}>
                        <button
                            onClick={switchToOneShots}
                            className={`btn btn-tab-green btnTabScroll ${styles.btnTab} ${!isCampaignsSelected ? styles.active : ""}`}
                            disabled={isLoadingOneShots && !isCampaignsSelected}
                        >
                            Ваншоты <span className={styles.amount}>{amountOneShots}</span>
                        </button>
                        <button
                            onClick={switchToCampaigns}
                            className={`btn btn-tab-green btnTabScroll ${styles.btnTab} ${isCampaignsSelected ? styles.active : ""}`}
                            disabled={isLoadingCampaigns && isCampaignsSelected}
                        >
                            Кампейны <span className={styles.amount}>{amountCampaigns}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Карточки */}
            <Cards cards={currentCards} type={currentType} cardsPerPage={pageSize} />

            {/* Пагинация */}
            <FadeIn delay={200}>
                <div className="d-flex justify-content-center">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={changePage}
                        maxVisible={3}
                    />
                </div>
            </FadeIn>

            {/* Лоадеры/ошибки */}
            {(isLoadingOneShots && !isCampaignsSelected) || (isLoadingCampaigns && isCampaignsSelected) ? (
                <p className="par2 text-center mt-3">Загрузка...</p>
            ) : null}

            {error && <p className="text-danger text-center">{error}</p>}
        </section>
    );
};

export default CatalogSection;
